// Creator dashboard: claim, earnings, and per game deploys, community and playtime. Creator powers come from
// accounts: a game's creator is `creator.claimedBy` on its catalogue entry (docs/build/CONTRACTS.md, Roles).

import { api, ApiError, isMissingEndpoint, loadTokens } from "../api.ts";
import { getCatalogue, refreshCatalogue, type Catalogue, type Creator, type Game } from "../catalogue.ts";
import { clear, errorBox, fmtDate, h, link, section, toast } from "../dom.ts";
import { formatCents, formatPrice, payoutDisplay, rateDisplay } from "../money.ts";
import { navigate, setTitle } from "../router.ts";
import { loadMe, type Me } from "../session.ts";
import { creatorAgeCopy, creatorAllowed } from "../creator-age.ts";
import { selling } from "../selling.ts";
import { gameCommunity, gameDeploy, gamePlaytime } from "./dash-game.ts";
import { gameClipsDash } from "./dash-clips.ts";
import { gameAnalytics } from "./dash-analytics.ts";
import { releasePanel } from "./dash-release.ts";
import { localNetworkBox, localNetworkQueue } from "./dash-localnet.ts";
import { isAssetModerator, myAssetsView, reviewQueueView } from "./dash-assets.ts";
import { isCommunityModerator, moderatorQueueView } from "../mod-queue.ts";
import { checkHandle, HANDLE_RULES, handleMessage, handleProblem, parseAvailable } from "../handles.ts";

/**
 * Accounts' claim flow (platform track, wave 2): a claim goes to a moderator, approval sets `claimedBy`. On an
 * accounts build without it, the page says claiming isn't open yet (GET /me/claims is missing).
 */
export const CLAIM_PATH = (creatorId: string) => `/catalogue/creators/${encodeURIComponent(creatorId)}/claims`;

export interface Mine {
  me: Me;
  cat: Catalogue;
  creators: Creator[];
  games: Game[];
}

/**
 * Your creators and games. `owned` is accounts' `GET /users/:id/creators` (fresh after a claim is approved; the
 * catalogue is cached for a minute); without it, the catalogue's `claimedBy`.
 */
export function mine(me: Pick<Me, "id">, cat: Pick<Catalogue, "creators" | "games">, owned?: string[]): Omit<Mine, "me" | "cat"> {
  const creators = owned ? cat.creators.filter((c) => owned.includes(c.id)) : cat.creators.filter((c) => c.claimedBy === me.id);
  // a handle claimed without a game (wave 6) may not be in the catalogue's creator list yet
  for (const id of owned ?? []) if (!creators.some((c) => c.id === id)) creators.push({ id, name: id.replace(/^cr_/, ""), url: null, claimedBy: me.id });
  const ids = new Set(creators.map((c) => c.id));
  return { creators, games: cat.games.filter((g) => ids.has(g.creator.id) || g.creator.claimedBy === me.id) };
}

/** The dashboard's top links. Earnings only while coins are sold (wave 9). */
export function dashNav(isOn: boolean): [string, string][] {
  return [["/dashboard", "Overview"], ...(isOn ? [["/dashboard/earnings", "Earnings"] as [string, string]] : []), ["/dashboard/claim", "Claim a game"], ["/dashboard/assets", "Assets"]];
}

/** Which section a path shows: without selling, /dashboard/earnings shows the overview. */
export function dashSection(name: string, isOn: boolean): string {
  return name === "earnings" && !isOn ? "overview" : name;
}

/** The signed-out lead and the no-games note, without earnings or payouts when nothing is sold. */
export function dashCopy(isOn: boolean): { lead: string; claim: string } {
  return isOn
    ? { lead: "Claim your games, ship new versions, answer your players and see what you've earned.", claim: "If your game is in the Banger catalogue, claim it to post devlogs, answer bug reports, ship versions and get paid." }
    : { lead: "Claim your games, ship new versions and answer your players.", claim: "If your game is in the Banger catalogue, claim it to post devlogs, answer bug reports and ship versions." };
}

export async function dashboardPage(params: Record<string, string>, query: URLSearchParams = new URLSearchParams()): Promise<HTMLElement> {
  setTitle("Creator dashboard");
  const me = await loadMe();
  if (!me || !loadTokens()) {
    return h(
      "div",
      { class: "empty" },
      h("h1", null, "Creator dashboard"),
      h("p", { class: "lead" }, dashCopy(selling()).lead),
      link(`/signin?next=${encodeURIComponent(location.pathname)}`, { class: "btn" }, "Sign in"),
    );
  }
  // Creators are 18+: a 13-17 account sees a kind note instead of the creator actions (the services refuse them too).
  if (!creatorAllowed(me)) return creatorAgeNote();
  const cat = await getCatalogue();
  const owned = await api<{ creators: { id: string }[] }>("accounts", `/users/${encodeURIComponent(me.id)}/creators`)
    .then((r) => r.creators.map((c) => c.id))
    .catch((e) => {
      if (isMissingEndpoint(e)) return undefined;
      throw e;
    });
  const m: Mine = { me, cat, ...mine(me, cat, owned) };
  const game = params.game ? m.games.find((g) => g.id === params.game) : undefined;
  const sectionName = dashSection(params.section ?? (params.game ? "deploy" : "overview"), selling());
  if (params.section === "earnings" && sectionName !== "earnings") history.replaceState(null, "", "/dashboard");
  // moderators see the local-network queue (the list answers 403 for everyone else)
  if (reviewerFor?.id !== m.me.id) reviewerFor = { id: m.me.id, ok: api("accounts", "/catalogue/local-network?limit=1").then(() => true, () => false) };
  const reviewer = await reviewerFor.ok;
  const [assetModerator, communityModerator] = await Promise.all([isAssetModerator(m.me.id), isCommunityModerator(m.me.id)]);

  let body: HTMLElement;
  if (params.game && !game) body = h("div", null, h("h1", null, "Not your game"), h("p", { class: "dim" }, "Only the creator who claimed a game can manage it."), link("/dashboard/claim", { class: "btn" }, "Claim a game"));
  else if (game && sectionName === "community") body = gameCommunity(game);
  else if (game && sectionName === "playtime") body = gamePlaytime(game);
  else if (game && sectionName === "clips") body = gameClipsDash(game);
  else if (game && sectionName === "analytics") body = gameAnalytics(game);
  else if (game)
    body = h(
      "div",
      null,
      gameDeploy(game),
      h("p", { class: "dash-links" }, link(`/dashboard/g/${game.id}/analytics`, { class: "btn small ghost" }, "Analytics: players, sessions, who comes back")),
      releasePanel(game),
      localNetworkBox(game),
    );
  else if (sectionName === "local-network") body = localNetworkQueue(cat);
  else if (sectionName === "claim") body = claimView(m);
  else if (sectionName === "assets") body = h("div", null, m.creators.length ? null : handleClaim(), myAssetsView());
  else if (sectionName === "review") body = reviewQueueView();
  else if (sectionName === "moderation") body = moderatorQueueView(m.me.id, query);
  else if (sectionName === "earnings") body = earningsView();
  else body = overview(m);

  const here = location.pathname;
  const nav = (href: string, text: string) => link(href, { "aria-current": here === href ? "page" : null }, text);
  return h(
    "div",
    { class: "dash" },
    h(
      "nav",
      { class: "dash-nav", "aria-label": "Dashboard" },
      dashNav(selling()).map(([href, text]) => nav(href, text)),
      communityModerator ? nav("/dashboard/moderation", "Reports") : null,
      assetModerator ? nav("/dashboard/review", "Asset review") : null,
      reviewer ? nav("/dashboard/local-network", "Local network requests") : null,
      m.games.map((g) => [
        h("span", { class: "group" }, g.title),
        nav(`/dashboard/g/${g.id}`, `${g.title}: Deploys`),
        nav(`/dashboard/g/${g.id}/community`, `${g.title}: Community`),
        nav(`/dashboard/g/${g.id}/clips`, `${g.title}: Clips`),
        nav(`/dashboard/g/${g.id}/analytics`, `${g.title}: Analytics`),
      ]),
    ),
    h("div", { class: "dash-main" }, body),
  );
}

function creatorAgeNote(): HTMLElement {
  const c = creatorAgeCopy(selling());
  return h(
    "div",
    { class: "empty creator-age" },
    h("h1", null, c.title),
    h("p", { class: "lead" }, c.lead),
    h("p", null, c.later),
    h("p", { class: "dim" }, c.meanwhile),
    h("p", null, link("/browse", { class: "btn" }, "Browse games"), " ", link("/store", { class: "btn ghost" }, "See assets")),
  );
}

/** Asked once per signed-in user per page load. */
let reviewerFor: { id: string; ok: Promise<boolean> } | null = null;

function overview(m: Mine): HTMLElement {
  return h(
    "div",
    null,
    h("h1", null, "Creator dashboard"),
    m.games.length === 0
      ? h(
          "div",
          { class: "panel" },
          h("h2", null, "No games yet"),
          h("p", null, dashCopy(selling()).claim),
          link("/dashboard/claim", { class: "btn" }, "Claim your game"),
        )
      : null,
    m.games.length === 0 && m.creators.length === 0 ? handleClaim() : null,
    // a handle without a game (wave 6): say who they are on thrdm and where publishing lives
    m.games.length === 0 && m.creators.length > 0
      ? h(
          "div",
          { class: "panel" },
          h("h2", null, "Your creator handle"),
          h("p", null, m.creators.map((c, i) => [i ? ", " : "", h("code", null, c.id)]), ". Your assets are published under it, as ", h("code", null, `${m.creators[0]!.id.replace(/^cr_/, "")}/<name>`), "."),
          link("/dashboard/assets", { class: "btn small" }, "Your assets"),
        )
      : null,
    m.games.length === 0
      ? null
      : [
          // earnings and the identity check (needed for payouts) only while coins are sold (wave 9)
          selling() ? section(null, async (body) => {
            const e = await api<Earnings>("money", "/creators/me/earnings").catch(() => null);
            clear(
              body,
              h(
                "div",
                { class: "kpis" },
                kpi("Games", String(m.games.length)),
                kpi("Unpaid earnings", e ? formatPrice(e.unpaid.coins) : "—"),
                kpi("Identity check", e ? (e.identity_verified ? "Verified" : "Not verified") : "—"),
              ),
            );
          }) : h("div", { class: "kpis" }, kpi("Games", String(m.games.length))),
          h(
            "section",
            { class: "panel" },
            h("h2", null, "Your games"),
            h(
              "table",
              null,
              h("thead", null, h("tr", null, h("th", null, "Game"), h("th", null, "Creator page"), h("th", null, h("span", { class: "sr-only" }, "Manage")))),
              h(
                "tbody",
                null,
                m.games.map((g) =>
                  h(
                    "tr",
                    null,
                    h("td", null, link(`/g/${g.id}`, null, g.title)),
                    h("td", null, link(`/c/${g.creator.id}`, null, g.creator.name)),
                    h(
                      "td",
                      null,
                      h(
                        "div",
                        { class: "actions" },
                        link(`/dashboard/g/${g.id}`, { class: "btn small ghost" }, "Deploys"),
                        link(`/dashboard/g/${g.id}/community`, { class: "btn small ghost" }, "Community"),
                        link(`/dashboard/g/${g.id}/clips`, { class: "btn small ghost" }, "Clips"),
                        link(`/dashboard/g/${g.id}/analytics`, { class: "btn small ghost" }, "Analytics"),
                      ),
                    ),
                  ),
                ),
              ),
            ),
          ),
        ],
  );
}

export function kpi(k: string, v: string, sub?: string): HTMLElement {
  return h("div", { class: "kpi" }, h("div", { class: "k" }, k), h("div", { class: "v" }, v), sub ? h("div", { class: "dim small" }, sub) : null);
}

export interface Claim {
  id: string;
  creatorId: string;
  status: "pending" | "approved" | "rejected" | "withdrawn";
  evidence: string | null;
  note: string | null;
  createdAt: number;
  decidedAt: number | null;
}

function claimView(m: Mine): HTMLElement {
  const host = h("div");
  const load = () =>
    clear(
      host,
      section(null, async (body) => {
        let claims: Claim[] = [];
        let open = true;
        try {
          claims = (await api<{ claims: Claim[] }>("accounts", "/me/claims")).claims;
        } catch (e) {
          if (!isMissingEndpoint(e)) throw e;
          open = false;
        }
        const pendingFor = new Map(claims.filter((c) => c.status === "pending").map((c) => [c.creatorId, c]));
        const nameOf = (id: string) => m.cat.creators.find((c) => c.id === id)?.name ?? id;
        const list = h("div");
        const q = h("input", { type: "search", placeholder: "Find your studio or name", "aria-label": "Find a creator" });
        const draw = () => {
          const needle = q.value.trim().toLowerCase();
          const rows = m.cat.creators.filter((c) => !needle || c.name.toLowerCase().includes(needle));
          clear(
            list,
            h(
              "table",
              null,
              h("thead", null, h("tr", null, h("th", null, "Creator"), h("th", null, "Games"), h("th", null, h("span", { class: "sr-only" }, "Claim")))),
              h(
                "tbody",
                null,
                rows.map((c) =>
                  h(
                    "tr",
                    null,
                    h("td", null, link(`/c/${c.id}`, null, c.name)),
                    h("td", { class: "small" }, m.cat.games.filter((g) => g.creator.id === c.id).map((g) => g.title).join(", ")),
                    h(
                      "td",
                      null,
                      m.creators.some((x) => x.id === c.id)
                        ? h("span", { class: "badge ok" }, "Yours")
                        : c.claimedBy
                          ? h("span", { class: "badge" }, "Claimed")
                          : pendingFor.has(c.id)
                            ? h("span", { class: "badge warn" }, "In review")
                            : open
                              ? claimForm(c, load)
                              : h("span", { class: "dim small" }, "Claiming opens soon"),
                    ),
                  ),
                ),
              ),
            ),
          );
        };
        q.addEventListener("input", draw);
        draw();
        clear(
          body,
          claims.length
            ? h(
                "div",
                { class: "panel" },
                h("h2", null, "Your claims"),
                h(
                  "table",
                  null,
                  h("thead", null, h("tr", null, h("th", null, "Creator"), h("th", null, "Status"), h("th", null, "Sent"), h("th", null, h("span", { class: "sr-only" }, "Actions")))),
                  h(
                    "tbody",
                    null,
                    claims.map((c) =>
                      h(
                        "tr",
                        null,
                        h("td", null, nameOf(c.creatorId)),
                        h("td", null, h("span", { class: `badge ${c.status === "approved" ? "ok" : c.status === "pending" ? "warn" : c.status === "rejected" ? "bad" : ""}` }, CLAIM_STATUS[c.status] ?? c.status), c.note ? h("div", { class: "dim small" }, c.note) : null),
                        h("td", { class: "small" }, fmtDate(c.createdAt)),
                        h(
                          "td",
                          null,
                          c.status === "pending"
                            ? h(
                                "button",
                                {
                                  type: "button",
                                  class: "btn small ghost",
                                  onclick: async () => {
                                    await api("accounts", `/me/claims/${encodeURIComponent(c.id)}`, { method: "DELETE" }).catch((e) => toast(String(e)));
                                    load();
                                  },
                                },
                                "Withdraw",
                              )
                            : null,
                        ),
                      ),
                    ),
                  ),
                ),
              )
            : null,
          h("div", { class: "panel" }, h("h2", null, "Find your listing"), q, list),
        );
      }),
    );
  load();
  return h(
    "div",
    null,
    h("h1", null, "Claim a game"),
    h("p", { class: "lead" }, "Games on Banger are credited to their creators. Claim yours to manage it; our team reviews each claim before anything changes."),
    host,
  );
}

const CLAIM_STATUS: Record<string, string> = { pending: "In review", approved: "Approved", rejected: "Not approved", withdrawn: "Withdrawn" };

function claimForm(c: Creator, reload: () => void): HTMLElement {
  const out = h("div");
  const evidence = h("input", { maxlength: 500, placeholder: "Proof: a link or note", "aria-label": `Proof that ${c.name} is you` });
  const form = h(
    "form",
    {
      class: "actions",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        try {
          await api<{ claim: Claim }>("accounts", CLAIM_PATH(c.id), { body: evidence.value.trim() ? { evidence: evidence.value.trim() } : {} });
          refreshCatalogue();
          toast(`Claim for ${c.name} sent for review`);
          reload();
        } catch (x) {
          if (x instanceof ApiError && x.code === "already_yours") {
            refreshCatalogue();
            return navigate("/dashboard");
          }
          clear(out, form, errorBox(x));
        }
      },
    },
    evidence,
    h("button", { type: "submit", class: "btn small" }, "Claim"),
  );
  out.append(form);
  return out;
}

// ---- earnings ----------------------------------------------------------------------------------------------

export interface Earnings {
  creator: string;
  unpaid: { coins: number; display: string };
  /** `tips` (money wave 6): the part of `coins` that came from tips (after the usual 20% cut), already counted in `coins` */
  by_month: { month: string; coins: number; display: string; tips?: number }[];
  identity_verified: boolean;
}

export interface StatementLine {
  /** money wave 6; a tip's `item_id` is the game's id */
  kind?: "item" | "cosmetic" | "asset" | "tip" | string;
  item_id: string;
  role: "seller" | "affiliate" | string;
  sales: number;
  coins: number;
}

/** Subscription pool money for one game in one closed month (economy track), already in cents. */
export interface PoolLine {
  month: string;
  game: string;
  cents: number;
}

export interface Statement {
  month: string;
  status: "payable" | "held" | string;
  earned_coins: number;
  converted_coins: number;
  rate_cents_per_100_coins: number;
  amount_cents: number;
  amount_display: string;
  coin_cents?: number;
  pool_cents?: number;
  lines: StatementLine[];
  pool_lines?: PoolLine[];
  note?: string;
}

export function lineLabel(l: StatementLine, gameTitle: (id: string) => string = (id) => id): string {
  if (l.kind === "tip") return `Tips: ${gameTitle(l.item_id)}`;
  return l.role === "affiliate" ? `${l.item_id} (bought in your game)` : l.item_id;
}

/** Tips in a by-month row (a missing or malformed field reads as none). */
export function monthTips(m: { tips?: unknown }): number {
  return typeof m.tips === "number" && Number.isFinite(m.tips) && m.tips > 0 ? m.tips : 0;
}

interface MoneyConfig {
  payout_cents_per_100_coins?: number;
  splits?: Record<string, { platformBps: number; affiliateBps: number }>;
}

/** "You keep 80% of each in-game sale and 75% of cosmetics. ", from money's configured splits. */
export function shareText(cfg: MoneyConfig): string {
  const keep = (k: string) => {
    const s = cfg.splits?.[k];
    return s ? `${(10000 - s.platformBps - s.affiliateBps) / 100}%` : null;
  };
  const item = keep("item");
  const cosmetic = keep("cosmetic");
  const tip = keep("tip");
  if (!item) return "";
  return `You keep ${item} of each in-game sale${cosmetic ? ` and ${cosmetic} of cosmetics` : ""}${tip ? `, and ${tip === "100%" ? "all" : tip} of every tip` : ""}. `;
}

function earningsView(): HTMLElement {
  return h(
    "div",
    null,
    h("h1", null, "Earnings"),
    section(null, async (body) => {
      const [e, cfg, st] = await Promise.all([
        api<Earnings>("money", "/creators/me/earnings"),
        api<MoneyConfig>("money", "/config", { auth: false }),
        api<{ statements: Statement[] }>("money", "/creators/me/statements"),
      ]);
      const cat = await getCatalogue();
      const gameTitle = (id: string) => cat.games.find((g) => g.id === id)?.title ?? id;
      const rate = cfg.payout_cents_per_100_coins;
      if (typeof rate !== "number") throw new Error("money didn't report its payout rate");
      const anyTips = e.by_month.some((m) => monthTips(m) > 0);
      clear(
        body,
        h(
          "div",
          { class: "kpis" },
          kpi("Unpaid earnings", formatPrice(e.unpaid.coins)),
          kpi("Payout at today's rate", payoutDisplay(e.unpaid.coins, rate), rateDisplay(rate)),
          kpi("Identity check", e.identity_verified ? "Verified" : "Not verified", e.identity_verified ? "Payouts go out monthly" : "Needed before we can pay you; earnings are held until then"),
        ),
        h(
          "p",
          { class: "dim small" },
          `${shareText(cfg)}Earned coins can't be spent: each month they convert to money at ${rateDisplay(rate)}.`,
        ),
        h(
          "div",
          { class: "panel" },
          h("h2", null, "By month"),
          e.by_month.length
            ? h(
                "table",
                null,
                h("thead", null, h("tr", null, h("th", null, "Month"), h("th", { class: "num" }, "Earned"), anyTips ? h("th", { class: "num" }, "Of which tips") : null, h("th", { class: "num" }, "At payout rate"))),
                h(
                  "tbody",
                  null,
                  [...e.by_month]
                    .reverse()
                    .map((m) =>
                      h(
                        "tr",
                        null,
                        h("td", null, m.month),
                        h("td", { class: "num" }, formatPrice(m.coins)),
                        anyTips ? h("td", { class: "num" }, monthTips(m) ? formatPrice(monthTips(m)) : "—") : null,
                        h("td", { class: "num" }, payoutDisplay(m.coins, rate)),
                      ),
                    ),
                ),
              )
            : h("p", { class: "dim" }, "No sales yet."),
          anyTips ? h("p", { class: "dim small" }, "Tips are counted in Earned. Players tip in Banger; all of each tip is yours, and tips can't be refunded.") : null,
        ),
        h("div", { class: "panel" }, h("h2", null, "Payout statements"), st.statements.length ? st.statements.map((x) => statementView(x, gameTitle)) : h("p", { class: "dim" }, "Statements are written after each month closes.")),
      );
    }),
  );
}

function statementView(s: Statement, gameTitle: (id: string) => string): HTMLElement {
  const pool = s.pool_lines ?? [];
  return h(
    "details",
    { class: "statement" },
    h(
      "summary",
      null,
      h("strong", null, s.month),
      " · ",
      s.amount_display,
      " ",
      h("span", { class: `badge ${s.status === "payable" ? "ok" : "warn"}` }, s.status === "payable" ? "Payable" : "Held: identity check"),
    ),
    h(
      "table",
      null,
      h("thead", null, h("tr", null, h("th", null, "Line"), h("th", { class: "num" }, "Sales"), h("th", { class: "num" }, "Earned"))),
      h(
        "tbody",
        null,
        s.lines.map((l) =>
          h("tr", { class: l.kind === "tip" ? "tip-line" : null }, h("td", null, l.kind === "tip" ? h("span", { class: "badge" }, "Tip") : null, l.kind === "tip" ? " " : null, lineLabel(l, gameTitle)), h("td", { class: "num" }, l.sales), h("td", { class: "num" }, formatPrice(l.coins))),
        ),
        pool.map((l) => h("tr", null, h("td", null, `Subscription pool: ${gameTitle(l.game)} (${l.month})`), h("td", { class: "num" }, ""), h("td", { class: "num" }, formatCents(l.cents)))),
      ),
    ),
    h(
      "p",
      { class: "dim small" },
      `Coins: ${formatPrice(s.earned_coins)} earned, ${formatPrice(s.converted_coins)} converted at ${rateDisplay(s.rate_cents_per_100_coins)}`,
      s.coin_cents !== undefined ? ` = ${formatCents(s.coin_cents)}` : "",
      s.pool_cents !== undefined ? `; pool ${formatCents(s.pool_cents)}` : "",
      `. Total ${s.amount_display}. ${s.note ?? ""}`,
    ),
    h("button", { type: "button", class: "btn small ghost", onclick: () => downloadCsv(s.month) }, "Download CSV"),
  );
}

async function downloadCsv(month: string): Promise<void> {
  try {
    const res = await api<Response>("money", `/creators/me/statements/${month}?format=csv`, { raw: true });
    if (!res.ok) throw new ApiError(res.status, "csv_failed", "Couldn't get the statement");
    const url = URL.createObjectURL(await res.blob());
    const a = h("a", { href: url, download: `statement-${month}.csv` });
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    toast(e instanceof Error ? e.message : String(e));
  }
}

// ---- a creator handle without a game -----------------------------------------------------------------------------

/**
 * "Claim a creator handle" for a signed-in user with no creator yet: publish assets under `cr_<handle>` without a game
 * in the catalogue (accounts wave 6). Availability is checked while typing; accounts decides on submit.
 */
function handleClaim(): HTMLElement {
  const handle = h("input", { id: "cr-handle", required: true, minlength: 3, maxlength: 32, autocomplete: "off", spellcheck: "false", placeholder: "my-studio", "aria-describedby": "cr-handle-rules cr-handle-status" }) as HTMLInputElement;
  const name = h("input", { id: "cr-name", maxlength: 80, autocomplete: "organization", placeholder: "My Studio" }) as HTMLInputElement;
  const status = h("p", { id: "cr-handle-status", class: "handle-status small", "aria-live": "polite" });
  const msg = h("div", { "aria-live": "polite" });
  let timer = 0;
  let asked = "";
  const check = () => {
    window.clearTimeout(timer);
    const v = handle.value.trim();
    const problem = handleProblem(v);
    if (!v) return clear(status);
    if (problem) return clear(status, h("span", { class: "error" }, problem));
    clear(status, h("span", { class: "dim" }, "Checking…"));
    timer = window.setTimeout(async () => {
      asked = v;
      try {
        const r = parseAvailable(await api("accounts", `/creators/available?handle=${encodeURIComponent(v)}`), v);
        if (!r || asked !== handle.value.trim()) return; // a reply for an older value
        clear(status, r.available ? h("span", { class: "badge ok" }, `cr_${v} is free`) : h("span", { class: "error" }, handleMessage(r.reason) ?? "That handle isn't available."));
      } catch (e) {
        if (asked === handle.value.trim()) clear(status, isMissingEndpoint(e) ? h("span", { class: "dim" }, "We'll check it when you claim.") : h("span", { class: "dim" }, "Couldn't check right now; we'll check when you claim."));
      }
    }, 300);
  };
  handle.addEventListener("input", () => {
    const lower = handle.value.toLowerCase();
    if (lower !== handle.value) handle.value = lower;
    check();
  });
  const submit = h("button", { type: "submit", class: "btn" }, "Claim this handle");
  const form = h(
    "form",
    {
      class: "stack",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        const v = handle.value.trim();
        if (checkHandle(v)) return clear(msg, h("p", { class: "error", role: "alert" }, handleMessage("invalid_handle")));
        submit.setAttribute("disabled", "");
        try {
          await api("accounts", "/creators", { body: name.value.trim() ? { handle: v, name: name.value.trim() } : { handle: v } });
          refreshCatalogue();
          toast(`You're cr_${v} on Banger`);
          navigate(location.pathname, true);
        } catch (x) {
          const words = x instanceof ApiError ? handleMessage(x.code) : null;
          clear(msg, words ? h("p", { class: "error", role: "alert" }, words) : errorBox(x));
        } finally {
          submit.removeAttribute("disabled");
        }
      },
    },
    h("div", { class: "field" }, h("label", { for: "cr-handle" }, "Handle"), handle, h("p", { id: "cr-handle-rules", class: "dim small" }, HANDLE_RULES, " It becomes your creator address (cr_ plus the handle) and the first part of your assets' ids."), status),
    h("div", { class: "field" }, h("label", { for: "cr-name" }, "Display name (optional)"), name),
    submit,
    msg,
  );
  return h(
    "section",
    { class: "panel handle-claim" },
    h("h2", null, "Claim a creator handle"),
    h("p", null, "Publish assets under your own name, even before you have a game on Banger. If you made a game that's already listed, ", link("/dashboard/claim", null, "claim it"), " instead."),
    form,
  );
}
